import { useRef, useState } from "react";
import axios from "axios";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SmsConsent } from "@/components/SmsConsent";
import { API_URL, BUSINESS } from "@/lib/constants";

const initialFields = { full_name: "", email: "", phone: "" };

const validate = (fields) => {
  const errors = {};
  if (!fields.full_name.trim()) errors.full_name = "Please enter your full name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim()))
    errors.email = "Please enter a valid email address.";
  if (!/^[+()\-.\s\d]{7,}$/.test(fields.phone.trim()))
    errors.phone = "Please enter a valid mobile phone number.";
  return errors;
};

export const CreditReviewForm = () => {
  const [fields, setFields] = useState(initialFields);
  const [smsConsent, setSmsConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submittingRef = useRef(false);

  const setField = (key) => (e) => {
    setFields({ ...fields, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const errs = validate(fields);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/checkout-leads`, {
        full_name: fields.full_name.trim(),
        email: fields.email.trim(),
        phone: fields.phone.trim(),
        sms_consent: smsConsent,
        page_url: window.location.href,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error("We couldn't submit your information. Please try again or contact J11 Consulting.");
      submittingRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="review-form-card"
      className="relative rounded-2xl border border-white/10 bg-[#17171C] p-7 text-left shadow-xl shadow-black/40 sm:p-9"
    >
      <div className="absolute inset-x-8 top-0 h-1 rounded-b-full bg-[#C9A227]" />
      {submitted ? (
        <motion.div
          data-testid="review-success-message"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="flex min-h-[420px] flex-col items-center justify-center text-center"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#C9A227]/10">
            <CheckCircle2 className="h-8 w-8 text-[#D8B54A]" />
          </span>
          <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight text-[#F2EEE3]">
            Thank You — Your Information Has Been Received
          </h3>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#B8B2A6]">
            A member of the {BUSINESS.shortName} team will review your information and contact you
            regarding the next steps.
          </p>
          <p className="mt-3 max-w-sm text-[13px] leading-relaxed text-[#9E978A]">
            Please make sure the phone number and email address you provided are accurate so our
            team can reach you.
          </p>
        </motion.div>
      ) : (
        <>
          <h3 className="font-heading text-2xl font-bold tracking-tight text-[#F2EEE3] sm:text-3xl" data-testid="review-form-heading">
            Request Your Credit Review
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[#9E978A]">
            Enter your information below to begin. A member of the {BUSINESS.shortName} team will
            contact you regarding next steps.
          </p>
          <form onSubmit={handleSubmit} noValidate className="mt-7 space-y-5" data-testid="review-form">
            <div>
              <Label htmlFor="review-name" className="text-sm font-medium text-[#F2EEE3]">
                Full Name
              </Label>
              <Input
                id="review-name"
                data-testid="review-name-input"
                value={fields.full_name}
                onChange={setField("full_name")}
                placeholder="Jane Smith"
                autoComplete="name"
                className="mt-1.5 h-11 border-white/20 bg-[#1D1D23] text-[#F2EEE3] focus-visible:ring-[#C9A227]"
              />
              {errors.full_name && (
                <p className="mt-1.5 text-xs text-red-400" data-testid="review-name-error">{errors.full_name}</p>
              )}
            </div>
            <div>
              <Label htmlFor="review-email" className="text-sm font-medium text-[#F2EEE3]">
                Email Address
              </Label>
              <Input
                id="review-email"
                type="email"
                data-testid="review-email-input"
                value={fields.email}
                onChange={setField("email")}
                placeholder="jane@example.com"
                autoComplete="email"
                className="mt-1.5 h-11 border-white/20 bg-[#1D1D23] text-[#F2EEE3] focus-visible:ring-[#C9A227]"
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-400" data-testid="review-email-error">{errors.email}</p>
              )}
            </div>
            <div>
              <Label htmlFor="review-phone" className="text-sm font-medium text-[#F2EEE3]">
                Mobile Phone Number
              </Label>
              <Input
                id="review-phone"
                type="tel"
                data-testid="review-phone-input"
                value={fields.phone}
                onChange={setField("phone")}
                placeholder="(555) 123-4567"
                autoComplete="tel"
                className="mt-1.5 h-11 border-white/20 bg-[#1D1D23] text-[#F2EEE3] focus-visible:ring-[#C9A227]"
              />
              {errors.phone && (
                <p className="mt-1.5 text-xs text-red-400" data-testid="review-phone-error">{errors.phone}</p>
              )}
            </div>
            <SmsConsent
              id="review-sms-consent"
              testId="review-sms-consent-checkbox"
              checked={smsConsent}
              onCheckedChange={setSmsConsent}
            />
            <button
              type="submit"
              data-testid="review-submit-button"
              disabled={submitting}
              className="group flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-[#E7C766] to-[#AE8924] text-sm font-semibold text-black shadow-md shadow-[#C9A227]/20 transition-colors duration-200 hover:from-[#F2D47E] hover:to-[#C49B2E] disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Request My Credit Review
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
            <p className="text-center text-[13px] leading-relaxed text-[#9E978A]" data-testid="review-no-payment-note">
              No payment is required to request your review.
            </p>
          </form>
        </>
      )}
    </div>
  );
};
